import "./StaticBookingCalendar.style.css";

import { Box, Skeleton } from "@mui/material";
import React, { FC, useMemo } from "react";

import BookCalendarIcon from "../Icons/BookCalendarIcon";
import { StaticDatePicker } from "@mui/x-date-pickers";
import { addMothsToDate } from "../../utils";
import { fromUnixToDateString } from "../../utils/unix-timestamp";

interface ISelectableMonthProps {
  date?: Date;
  onSelected?: (value: Date) => void;
  icon?: React.ElementType<any>;
  daysCheck?: any;
  currentDay?: any;
  fullMonthDaysList?: any;
  handleMonthChange? (newMonth: Date): void;
  loading?: boolean;
}

const weekNameFormatter = (abbr: string) => {
  switch (abbr) {
  case "Su":
    return "Sun";
  case "Mo":
    return "Mon";
  case "Tu":
    return "Tue";
  case "We":
    return "Wed";
  case "Th":
    return "Thu";
  case "Fr":
    return "Fri";
  case "Sa":
    return "Sat";
  default:
    return abbr;
  }
};

const createDisabledDatesMap = (fullMonthDaysList: any[]) => {
  const map = new Map();

  fullMonthDaysList?.forEach((item) => {
    if (item.disabled) {
      const key = `${item.label}-${item.month}`;

      map.set(key, true);
    }
  });

  return map;
};

const StaticBookingCalendar: FC<ISelectableMonthProps> = ({ onSelected, icon, currentDay, fullMonthDaysList,
  handleMonthChange, loading }) => {
  const disabledDatesMap = useMemo(() => createDisabledDatesMap(fullMonthDaysList), [fullMonthDaysList]);
  const handleSelectDate = (newValue: Date | null) => {
    if (newValue) {
      onSelected && onSelected(newValue);
    }
  };

  const isDisabledDate = (date: Date) => {
    const currentCalendarDate = date.getDate().toString().padStart(2, "0");

    const monthInLongFormat = date.toLocaleString("default", { month: "long" });

    const key = `${currentCalendarDate}-${monthInLongFormat}`;

    return disabledDatesMap.has(key);
  };

  const shouldDisableDate = (date: Date) => {
    const isDisabled = isDisabledDate(date);
    const isCurrentDay = date.getTime() === new Date(+currentDay * 1000).getTime();

    return isDisabled && !isCurrentDay;
  };

  return <>
    <StaticDatePicker
      views={["day"]}
      minDate={new Date()}
      maxDate={addMothsToDate(new Date(), 5)}
      className="static-booking-calendar"
      displayStaticWrapperAs="desktop"
      value={new Date(fromUnixToDateString(+currentDay, "GMT"))}
      shouldDisableDate={shouldDisableDate}
      dayOfWeekFormatter={(date) => {
        return weekNameFormatter(date);
      }}
      onChange={() => { }}
      onAccept={handleSelectDate}
      onMonthChange={(newMonth)=>handleMonthChange && handleMonthChange(newMonth)}
      openTo="month"
      showToolbar={false}
      components={{
        OpenPickerIcon: icon?icon:BookCalendarIcon,
      }}
      disabled={loading}
      loading={loading}
      renderLoading={() => <div style={{ width: "100%", height: "100%" }} className="flex flex-col gap-[1.1rem] mx-2">
        <Skeleton variant="rectangular" className="rounded-xl !h-[1.5rem]"/>
        <Skeleton variant="rectangular" className="rounded-xl !h-[1.5rem]"/>
        <Skeleton variant="rectangular" className="rounded-xl !h-[1.5rem]"/>
        <Skeleton variant="rectangular" className="rounded-xl !h-[1.5rem]"/>
        <Skeleton variant="rectangular" className="rounded-xl !h-[1.5rem]"/>
      </div>}
      renderInput={({ inputRef, inputProps, InputProps }) => (
        <Box className="flex items-center">
          <input
            className="w-0 p-0 border-none bg-none"
            ref={inputRef}
            {...inputProps}
            readOnly
          />
          {InputProps?.endAdornment}
        </Box>
      )}
    />
  </>;
};

export default StaticBookingCalendar;
